// src/contexts/SocketContext.js - WebSocket Context Provider
import React, { createContext, useContext, useState, useEffect } from 'react';
import socketManager from '../services/socket/SocketManager';
import storeInitializer from '../services/StoreInitializer';
import { initializeNotificationListener } from '../services/NotificationListener';

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  // Connection state
  const [connectionState, setConnectionState] = useState('disconnected'); // disconnected, connecting, connected, reconnecting, failed
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState(null);

  // Store initialization state
  const [storesInitialized, setStoresInitialized] = useState(false);
  const [storeInitializationError, setStoreInitializationError] = useState(null);

  // Set up connection state callbacks
  useEffect(() => {
    // Listen for connection state changes
    socketManager.setConnectionStateCallback((state) => {
      console.log('🔄 Socket connection state changed:', state);
      setConnectionState(state);
      
      // Clear errors on successful connection
      if (state === 'connected') {
        setError(null);
      }
    });

    // Listen for auth state changes
    socketManager.setAuthStateCallback(async (authenticated) => {
      console.log('🔐 Socket auth state changed:', authenticated);
      setIsAuthenticated(authenticated);
      
      // Initialize stores when authentication succeeds
      if (authenticated && !storesInitialized) {
        console.log('🚀 SocketContext: Authentication successful, initializing stores...');
        setStoreInitializationError(null);

        try {
          const result = await storeInitializer.initializeAllStores(socketManager);

          if (result.success) {
            setStoresInitialized(true);
            console.log('✅ SocketContext: All stores initialized successfully');
          } else {
            setStoreInitializationError(result.error || 'Some stores failed to initialize');
            console.warn('⚠️ SocketContext: Store initialization completed with errors:', result);
            // Still mark as initialized since some stores may have succeeded
            setStoresInitialized(true);
          }

          // Initialize notification listener (runs after stores)
          console.log('🔔 SocketContext: Initializing notification listener...');
          initializeNotificationListener(socketManager);
          console.log('✅ SocketContext: Notification listener initialized');
        } catch (error) {
          setStoreInitializationError(error.message || 'Store initialization failed');
          console.error('❌ SocketContext: Store initialization failed:', error);
        }
      }
      
      // Reset store initialization state when authentication fails
      if (!authenticated && storesInitialized) {
        console.log('🔄 SocketContext: Authentication lost, resetting store initialization state');
        setStoresInitialized(false);
        setStoreInitializationError(null);
      }
    });

    return () => {
      // Clean up callbacks
      socketManager.setConnectionStateCallback(null);
      socketManager.setAuthStateCallback(null);
    };
  }, [storesInitialized]); // Include storesInitialized in dependency array

  // Connection methods
  const connect = async (token) => {
    try {
      setError(null);
      const success = await socketManager.connect(token);
      if (!success) {
        setError('Failed to connect to real-time services');
      }
      return success;
    } catch (err) {
      setError(err.message || 'Connection error');
      return false;
    }
  };

  const disconnect = () => {
    socketManager.disconnect();
    setError(null);
    // Reset store state on manual disconnect
    setStoresInitialized(false);
    setStoreInitializationError(null);
  };

  const reconnect = async (token) => {
    try {
      setError(null);
      const success = await socketManager.reconnect(token);
      if (!success) {
        setError('Failed to reconnect to real-time services');
      }
      return success;
    } catch (err) {
      setError(err.message || 'Reconnection error');
      return false;
    }
  };

  // Event communication methods
  const emit = (event, data) => {
    return socketManager.emit(event, data);
  };

  const on = (event, callback) => {
    socketManager.on(event, callback);
  };

  const off = (event, callback) => {
    socketManager.off(event, callback);
  };

  const request = (event, data, timeout) => {
    return socketManager.request(event, data, timeout);
  };

  // Get connection status
  const getStatus = () => {
    return socketManager.getStatus();
  };

  // Get store initialization status
  const getStoreStatus = () => {
    return storeInitializer.getStatus();
  };

  // Manual store initialization (for fallback scenarios)
  const initializeStore = async (storeName) => {
    if (!isAuthenticated) {
      console.warn('⚠️ SocketContext: Cannot initialize store - not authenticated');
      return false;
    }
    
    return await storeInitializer.initializeStore(storeName, socketManager);
  };

  const value = {
    // State
    connectionState,
    isAuthenticated,
    error,
    
    // Store initialization state
    storesInitialized,
    storeInitializationError,
    
    // Methods
    connect,
    disconnect,
    reconnect,
    emit,
    on,
    off,
    request,
    getStatus,
    
    // Store methods
    getStoreStatus,
    initializeStore,
    
    // Direct access to socket manager if needed
    socketManager
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};